import React from "react";
import { bareTree, H, mix, snowCap } from "./kit";
import { range, seeded } from "./sceneUtils";

/*
 * Vinterkväll i Vasastan, delat av lucia- och julscenen: stenstadens tak,
 * Gustaf Vasa kyrka vid Odenplan, sekelskifteshus med adventsljus i
 * fönstren, snötyngda lindar och Stockholmslyktor.
 *
 * Allt tar emot `night`, färgen som husen blandas mot, så att samma hus
 * passar både lucias blå timme och julnattens mörkröda himmel.
 */

export const SNOW = "#eef1ff";
export const SNOW_SHADE = "#9aa4da";
export const LIT = "#ffcf7a";
export const WARM = "#ffdf9a";

/** Gradienter som komponenterna här använder. Renderas en gång per scen. */
export const VasastanDefs: React.FC = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <defs>
      <linearGradient id="vs-roofsnow" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor={SNOW_SHADE} />
      </linearGradient>
      <linearGradient id="vs-win" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffe9b0" />
        <stop offset="100%" stopColor="#f6a94a" />
      </linearGradient>
      <radialGradient id="vs-lamp" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffe2a8" stopOpacity="0.6" />
        <stop offset="40%" stopColor="#ffc46a" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#ffb04a" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="vs-floodlight" cx="50%" cy="100%" r="90%">
        <stop offset="0%" stopColor="#ffd9a0" stopOpacity="0.45" />
        <stop offset="60%" stopColor="#ffd9a0" stopOpacity="0.08" />
        <stop offset="100%" stopColor="#ffd9a0" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="vs-dome" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#4a4a58" />
        <stop offset="35%" stopColor="#2c2c38" />
        <stop offset="100%" stopColor="#111118" />
      </linearGradient>
    </defs>
  </svg>
);

/* ---------- Fönster med adventsljus ---------- */

const bridgePath = (x: number, y: number, w: number) => `M${x - w / 2} ${y} L${x} ${y - w * 0.42} L${x + w / 2} ${y} Z`;

/** Tänt fönster med adventsljusstake (deco 0), stjärna (1) eller bara varmt ljus (2). */
export const LitWindow: React.FC<{ x: number; y: number; w: number; h: number; deco: number }> = ({ x, y, w, h, deco }) => (
  <g>
    <rect x={x - 3} y={y - 3} width={w + 6} height={h + 6} rx={3} fill={LIT} opacity="0.22" filter="url(#toon-soft)" />
    <rect x={x} y={y} width={w} height={h} fill="url(#vs-win)" />
    {deco === 0 && (
      <g>
        <path d={bridgePath(x + w / 2, y + h - 0.8, w * 0.8)} fill="#4a2c12" opacity="0.8" />
        {range(7).map(i => {
          const u = i / 6;
          return <circle key={i} cx={x + w / 2 - w * 0.4 + u * w * 0.8} cy={y + h - 1.6 - (1 - Math.abs(u - 0.5) * 2) * w * 0.34} r={0.8} fill="#ffffff" />;
        })}
      </g>
    )}
    {deco === 1 && (
      <g>
        <path d={`M${x + w / 2} ${y} V${y + h * 0.2}`} stroke="#5a3a18" strokeWidth="0.4" />
        <circle cx={x + w / 2} cy={y + h * 0.38} r={w * 0.42} fill="#ffffff" opacity="0.4" filter="url(#toon-soft)" />
        <polygon
          points={range(10)
            .map(q => {
              const rr = q % 2 ? w * 0.13 : w * 0.3;
              const a = (Math.PI / 5) * q - Math.PI / 2;
              return `${(x + w / 2 + Math.cos(a) * rr).toFixed(2)},${(y + h * 0.38 + Math.sin(a) * rr).toFixed(2)}`;
            })
            .join(" ")}
          fill="#fffaf0"
        />
      </g>
    )}
    <path d={`M${x + w / 2} ${y} V${y + h} M${x} ${y + h * 0.36} H${x + w}`} stroke="#3a2a40" strokeWidth="0.7" opacity="0.6" />
  </g>
);

/* ---------- Stenstaden längst bort ---------- */

export interface Skyline {
  body: string;
  roofs: string;
  lit: string;
}

/**
 * Tak och fasader i stenstaden: mansardtak, gavlar och små tornspiror.
 * Inom `low` hålls husen låga så att något bakom (kyrkans kupol) syns.
 */
export const skyline = (seed: number, from: number, to: number, base: number, low: [number, number]): Skyline => {
  const rnd = seeded(seed);
  const body: string[] = [];
  const roofs: string[] = [];
  const lit: string[] = [];
  let x = from;
  while (x < to) {
    const w = 42 + rnd() * 56;
    const nearLow = x + w > low[0] && x < low[1];
    const h = nearLow ? 52 + rnd() * 18 : 64 + rnd() * 66;
    const top = base - h;
    const kind = rnd();
    body.push(`M${x.toFixed(1)} ${H} V${top.toFixed(1)} H${(x + w).toFixed(1)} V${H} Z`);
    // Snötäckt mansardtak på alla hus
    roofs.push(`M${(x - 1).toFixed(1)} ${top.toFixed(1)} L${(x + 5).toFixed(1)} ${(top - 9).toFixed(1)} H${(x + w - 5).toFixed(1)} L${(x + w + 1).toFixed(1)} ${top.toFixed(1)} Z`);
    if (kind > 0.55 && kind < 0.8) {
      // Jugendgavel mitt på fasaden
      const gx = x + w * 0.32;
      const gw = w * 0.36;
      body.push(`M${gx.toFixed(1)} ${(top + 1).toFixed(1)} L${(gx + gw / 2).toFixed(1)} ${(top - 24).toFixed(1)} L${(gx + gw).toFixed(1)} ${(top + 1).toFixed(1)} Z`);
      roofs.push(
        `M${(gx - 1.4).toFixed(1)} ${(top + 1).toFixed(1)} L${(gx + gw / 2).toFixed(1)} ${(top - 25.6).toFixed(1)} L${(gx + gw + 1.4).toFixed(1)} ${(top + 1).toFixed(1)} L${(gx + gw - 1.6).toFixed(1)} ${(top + 1).toFixed(1)} L${(gx + gw / 2).toFixed(1)} ${(top - 22).toFixed(1)} L${(gx + 1.6).toFixed(1)} ${(top + 1).toFixed(1)} Z`
      );
    } else if (kind >= 0.8 && !nearLow) {
      // Hörntorn med spira
      const tx = rnd() < 0.5 ? x + 3 : x + w - 15;
      body.push(`M${tx.toFixed(1)} ${(top + 2).toFixed(1)} V${(top - 22).toFixed(1)} H${(tx + 12).toFixed(1)} V${(top + 2).toFixed(1)} Z`);
      body.push(`M${(tx - 1).toFixed(1)} ${(top - 22).toFixed(1)} L${(tx + 6).toFixed(1)} ${(top - 46).toFixed(1)} L${(tx + 13).toFixed(1)} ${(top - 22).toFixed(1)} Z`);
      roofs.push(`M${(tx - 1.6).toFixed(1)} ${(top - 21.6).toFixed(1)} L${(tx + 6).toFixed(1)} ${(top - 30).toFixed(1)} L${(tx + 13.6).toFixed(1)} ${(top - 21.6).toFixed(1)} Z`);
    }
    // Skorstenar
    for (let c = 1 + Math.floor(rnd() * 2); c > 0; c--) {
      const cx = x + 8 + rnd() * (w - 16);
      body.push(`M${cx.toFixed(1)} ${(top - 6).toFixed(1)} v-10 h5 v10 Z`);
      roofs.push(`M${(cx - 0.8).toFixed(1)} ${(top - 16).toFixed(1)} h6.6 v1.8 h-6.6 Z`);
    }
    // Fönster: några tända
    for (let fy = top + 9; fy < base - 8; fy += 12) {
      for (let fx = x + 6; fx < x + w - 6; fx += 9.5) {
        if (rnd() < 0.2) lit.push(`M${fx.toFixed(1)} ${fy.toFixed(1)} h3 v4.2 h-3 Z`);
      }
    }
    x += w + (rnd() < 0.15 ? 8 + rnd() * 12 : 0);
  }
  return { body: body.join(" "), roofs: roofs.join(" "), lit: lit.join(" ") };
};

/* ---------- Gustaf Vasa kyrka ---------- */

const PLASTER = "#efe6d2";
const COPPER = "#6fae9a";
const GOLD = "#e9c46a";

/** Kupolen med lanternin och kors. (x, y) = kupolens fot, r = halva bredden. */
const Dome: React.FC<{ x: number; y: number; r: number; night: string; copper: string; lit: boolean }> = ({ x, y, r, night, copper, lit }) => {
  const h = r * 1.14;
  const top = y - h;
  const dark = mix(copper, night, 0.4);
  return (
    <g>
      {/* Mörk kupol med revben och runda takkupor */}
      <path d={`M${x - r} ${y} C ${x - r} ${y - h * 0.7}, ${x - r * 0.46} ${top}, ${x} ${top} C ${x + r * 0.46} ${top}, ${x + r} ${y - h * 0.7}, ${x + r} ${y} Z`} fill="url(#vs-dome)" />
      {[-0.62, -0.22, 0.22, 0.62].map((u, i) => (
        <path key={i} d={`M${x + u * r} ${y} Q ${x + u * r * 0.86} ${y - h * 0.62} ${x} ${top + 1}`} stroke="#5a5a68" strokeWidth={r * 0.025} fill="none" opacity="0.55" />
      ))}
      {[-0.5, 0, 0.5].map((u, i) => {
        const ox = x + u * r * 0.95;
        const oy = y - h * (u === 0 ? 0.42 : 0.34);
        const orad = r * (u === 0 ? 0.13 : 0.1);
        return (
          <g key={i}>
            <circle cx={ox} cy={oy} r={orad * 1.35} fill="#3c3c48" />
            <circle cx={ox} cy={oy} r={orad} fill={lit && u === 0 ? LIT : "#14141c"} opacity={lit && u === 0 ? 0.85 : 1} />
            <circle cx={ox} cy={oy} r={orad * 1.35} fill="none" stroke={GOLD} strokeWidth={r * 0.02} opacity="0.6" />
          </g>
        );
      })}
      {/* Förgyllda prydnader runt kupolens fot */}
      {range(7).map(i => (
        <circle key={i} cx={x - r * 0.9 + i * r * 0.3} cy={y - r * 0.05} r={r * 0.035} fill={GOLD} opacity="0.85" />
      ))}
      {/* Snö på kupolens nedre del och i toppen */}
      <path d={`M${x - r * 1.02} ${y} C ${x - r * 1.0} ${y - h * 0.2}, ${x - r * 0.92} ${y - h * 0.3}, ${x - r * 0.84} ${y - h * 0.36} Q ${x - r * 0.9} ${y - h * 0.16} ${x - r * 0.7} ${y - h * 0.05} Z`} fill={SNOW} opacity="0.85" />
      <path d={`M${x - r * 0.42} ${top + h * 0.1} Q ${x} ${top - h * 0.04} ${x + r * 0.42} ${top + h * 0.1} Q ${x} ${top + h * 0.05} ${x - r * 0.42} ${top + h * 0.1} Z`} fill={SNOW} opacity="0.9" />
      {/* Lanterninen i grön koppar med tända öppningar */}
      <rect x={x - r * 0.24} y={top - r * 0.5} width={r * 0.48} height={r * 0.52} fill={copper} />
      <rect x={x + r * 0.06} y={top - r * 0.5} width={r * 0.18} height={r * 0.52} fill={dark} opacity="0.6" />
      {[-0.12, 0.06].map((u, i) => (
        <path
          key={i}
          d={`M${x + u * r} ${top - r * 0.06} V${top - r * 0.34} a${r * 0.04} ${r * 0.04} 0 0 1 ${r * 0.08} 0 V${top - r * 0.06} Z`}
          fill={lit ? LIT : "#14141c"}
          opacity={lit ? 0.9 : 1}
        />
      ))}
      <rect x={x - r * 0.3} y={top - r * 0.56} width={r * 0.6} height={r * 0.07} fill={dark} />
      <path d={`M${x - r * 0.27} ${top - r * 0.56} Q ${x} ${top - r * 0.95} ${x + r * 0.27} ${top - r * 0.56} Z`} fill={copper} />
      <path d={`M${x - r * 0.28} ${top - r * 0.56} Q ${x - r * 0.1} ${top - r * 0.8} ${x} ${top - r * 0.84} Q ${x - r * 0.06} ${top - r * 0.7} ${x - r * 0.2} ${top - r * 0.6} Z`} fill={SNOW} opacity="0.75" />
      <rect x={x - r * 0.06} y={top - r * 1.02} width={r * 0.12} height={r * 0.12} fill={copper} />
      <circle cx={x} cy={top - r * 1.06} r={r * 0.06} fill={GOLD} />
      <path d={`M${x} ${top - r * 1.1} V${top - r * 1.42} M${x - r * 0.1} ${top - r * 1.3} H${x + r * 0.1}`} stroke={GOLD} strokeWidth={r * 0.045} strokeLinecap="round" />
    </g>
  );
};

/** Gustaf Vasa kyrkas kupol sedd över taken (bakom stenstaden). (x, y) = tamburens fot. */
export const GustafVasaDome: React.FC<{ x: number; y: number; night: string; glow: string }> = ({ x, y, night, glow }) => {
  const plaster = mix(PLASTER, night, 0.58);
  const plasterDark = mix(PLASTER, night, 0.72);
  return (
    <g>
      <circle cx={x} cy={y - 70} r={90} fill={glow} opacity="0.12" filter="url(#toon-softer)" />
      {/* Tamburen med parställda kolonner, rundbågefönster och urtavla */}
      <rect x={x - 32} y={y - 38} width="64" height="40" fill={plaster} />
      <rect x={x + 10} y={y - 38} width="22" height="40" fill={plasterDark} opacity="0.6" />
      {[-26, -19, 19, 26].map(cx => (
        <rect key={cx} x={x + cx - 1.6} y={y - 36} width="3.2" height="36" fill={plasterDark} opacity="0.7" />
      ))}
      {[-9, 9].map(wx => (
        <path key={wx} d={`M${x + wx - 3} ${y - 4} V${y - 20} A3 3 0 0 1 ${x + wx + 3} ${y - 20} V${y - 4} Z`} fill={LIT} opacity="0.85" />
      ))}
      <circle cx={x} cy={y - 27} r="5" fill={mix("#ffffff", night, 0.25)} />
      <path d={`M${x} ${y - 27} V${y - 30.4} M${x} ${y - 27} L${x + 2.4} ${y - 26}`} stroke="#2a2a33" strokeWidth="0.8" />
      <rect x={x - 35} y={y - 42} width="70" height="4" fill={plaster} />
      <path d={snowCap(x - 36, x + 36, y - 42.4, 1.6, 7)} fill={mix(SNOW, night, 0.3)} />
      <Dome x={x} y={y - 42} r={34} night={night} copper={mix(COPPER, night, 0.55)} lit />
    </g>
  );
};

/**
 * Gustaf Vasa kyrkas östfasad mot Odenplan (Agi Lindegren, 1906): portiken
 * med parställda kolonner och fronton, flyglar med rundbågefönster och
 * oxögon, gröna koppartak med små lanterniner, tamburen med urtavla och
 * den mörka kupolen med grön lanternin och förgyllt kors. Fasaden är
 * upplyst underifrån, fönstren lyser inför julottan och två julgranar
 * står vid trappan. (x, y) = trappans mitt vid marken.
 */
export const GustafVasaKyrka: React.FC<{ x: number; y: number; s?: number; night: string }> = ({ x, y, s = 1, night }) => {
  const plaster = mix(PLASTER, night, 0.3);
  const plasterShade = mix(PLASTER, night, 0.5);
  const plasterDeep = mix(PLASTER, night, 0.66);
  const stone = mix("#8a8378", night, 0.45);
  const copper = mix(COPPER, night, 0.42);
  const copperDark = mix(COPPER, night, 0.62);
  const snow = mix(SNOW, night, 0.08);
  const W2 = 196;
  const wingTop = -150;
  const P = 78;
  const colTop = -172;
  const ped = -188;
  const apex = -236;

  const arched = (wx: number, top: number, w: number, h: number, on: boolean, key: string | number) => (
    <g key={key}>
      {on && <rect x={wx - w / 2 - 3} y={top - 3} width={w + 6} height={h + 6} rx={w / 2} fill={LIT} opacity="0.25" filter="url(#toon-soft)" />}
      <path d={`M${wx - w / 2 - 2} ${top + h + 1} V${top + w / 2} A${w / 2 + 2} ${w / 2 + 2} 0 0 1 ${wx + w / 2 + 2} ${top + w / 2} V${top + h + 1} Z`} fill={plasterDeep} />
      <path d={`M${wx - w / 2} ${top + h} V${top + w / 2} A${w / 2} ${w / 2} 0 0 1 ${wx + w / 2} ${top + w / 2} V${top + h} Z`} fill={on ? "url(#vs-win)" : "#1a1420"} />
      <path d={`M${wx} ${top + h} V${top + 2} M${wx - w / 2} ${top + h * 0.55} H${wx + w / 2}`} stroke={plasterDeep} strokeWidth="0.9" opacity="0.8" />
      <rect x={wx - w / 2 - 3} y={top + h + 1} width={w + 6} height="2" fill={plaster} />
      <path d={snowCap(wx - w / 2 - 3, wx + w / 2 + 3, top + h + 0.6, 1.2, Math.round(wx))} fill={snow} />
    </g>
  );

  const oculus = (ox: number, oy: number, r: number, on: boolean, key: string | number) => (
    <g key={key}>
      <ellipse cx={ox} cy={oy} rx={r + 2} ry={r * 0.8 + 2} fill={plasterDeep} />
      <ellipse cx={ox} cy={oy} rx={r} ry={r * 0.8} fill={on ? LIT : "#1a1420"} opacity={on ? 0.85 : 1} />
    </g>
  );

  const lantern = (lx: number, base: number) => (
    <g>
      <rect x={lx - 9} y={base - 14} width="18" height="14" fill={copper} />
      <path d={`M${lx - 4} ${base - 2} V${base - 9} a2 2 0 0 1 4 0 V${base - 2} Z M${lx + 1} ${base - 2} V${base - 9} a2 2 0 0 1 4 0 V${base - 2} Z`} fill={LIT} opacity="0.7" />
      <path d={`M${lx - 11} ${base - 14} Q ${lx} ${base - 34} ${lx + 11} ${base - 14} Z`} fill={copper} />
      <path d={`M${lx + 2} ${base - 31} Q ${lx + 9} ${base - 24} ${lx + 11} ${base - 14} H${lx + 4} Z`} fill={copperDark} opacity="0.6" />
      <path d={`M${lx - 11} ${base - 14} Q ${lx - 6} ${base - 26} ${lx} ${base - 30} Q ${lx - 3} ${base - 22} ${lx - 6} ${base - 15} Z`} fill={snow} opacity="0.85" />
      <circle cx={lx} cy={base - 36} r="2.6" fill={GOLD} />
      <path d={`M${lx} ${base - 38} V${base - 44}`} stroke={GOLD} strokeWidth="1.4" />
    </g>
  );

  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      {/* Strålkastarljus på fasaden */}
      <ellipse cx="0" cy={-120} rx={W2 + 40} ry={200} fill="url(#vs-floodlight)" />

      {/* Tamburen och kupolen bakom portiken */}
      <rect x={-64} y={-286} width="128" height="110" fill={plasterShade} />
      <rect x={24} y={-286} width="40" height="110" fill={plasterDeep} opacity="0.6" />
      {[-56, -46, 46, 56].map(cx => (
        <g key={cx}>
          <rect x={cx - 3} y={-282} width="6" height="96" fill={plaster} opacity="0.85" />
          <rect x={cx - 4.4} y={-284} width="8.8" height="3" fill={plaster} />
        </g>
      ))}
      {[-30, 30].map(wx => arched(wx, -278, 12, 26, true, `t${wx}`))}
      {/* Urtavlan i tamburen, ovanför frontonen */}
      <circle cx="0" cy={-258} r="13" fill={plasterDeep} />
      <circle cx="0" cy={-258} r="10.5" fill={mix("#ffffff", night, 0.1)} />
      {range(12).map(i => {
        const a = (i * 30 * Math.PI) / 180;
        return <path key={i} d={`M${Math.sin(a) * 8} ${-258 - Math.cos(a) * 8} L${Math.sin(a) * 9.6} ${-258 - Math.cos(a) * 9.6}`} stroke="#2a2a33" strokeWidth={i % 3 === 0 ? 1.2 : 0.6} />;
      })}
      {/* Klockan är strax före midnatt: julnatt */}
      <path d={`M0 ${-258} l-0.6 -5.6 M0 ${-258} l-1.6 -7.6`} stroke="#1a1a22" strokeWidth="1.2" strokeLinecap="round" />
      <rect x={-70} y={-292} width="140" height="7" fill={plaster} />
      <rect x={-70} y={-285} width="140" height="2" fill={plasterDeep} />
      {/* Balustrad */}
      {range(18).map(i => (
        <rect key={i} x={-66 + i * 7.6} y={-300} width="3.2" height="8" rx="1.2" fill={plaster} />
      ))}
      <rect x={-70} y={-302} width="140" height="3" fill={plaster} />
      <path d={snowCap(-71, 71, -302.6, 2, 9)} fill={snow} />
      <Dome x={0} y={-302} r={68} night={night} copper={copper} lit />

      {/* Flyglarnas koppartak med lanterniner */}
      {[-1, 1].map(side => (
        <g key={side}>
          <path d={`M${side * (P + 4)} ${wingTop} L${side * (P + 20)} ${wingTop - 26} H${side * (W2 - 26)} L${side * (W2 + 4)} ${wingTop} Z`} fill={copper} />
          <path d={`M${side * (W2 - 26)} ${wingTop - 26} L${side * (W2 + 4)} ${wingTop} H${side * (W2 - 18)} Z`} fill={copperDark} opacity="0.55" />
          <path d={snowCap(Math.min(side * (P + 18), side * (W2 - 24)), Math.max(side * (P + 18), side * (W2 - 24)), wingTop - 27, 2.4, side + 30)} fill={snow} />
          <path d={`M${side * (P + 6)} ${wingTop - 1} L${side * (P + 12)} ${wingTop - 9} H${side * (W2 - 10)} L${side * (W2 + 2)} ${wingTop - 1} Z`} fill={snow} opacity="0.75" />
          {lantern(side * (W2 * 0.68 + 6), wingTop - 26)}
        </g>
      ))}

      {/* Flyglarna */}
      <rect x={-W2} y={wingTop} width={W2 * 2} height={-wingTop} fill={plaster} />
      <rect x={W2 - 30} y={wingTop} width="30" height={-wingTop} fill={plasterShade} opacity="0.7" />
      <rect x={-W2 - 3} y={wingTop - 6} width={W2 * 2 + 6} height="7" fill={plaster} />
      <rect x={-W2 - 3} y={wingTop + 1} width={W2 * 2 + 6} height="2" fill={plasterDeep} />
      <path d={snowCap(-W2 - 4, W2 + 4, wingTop - 6.4, 2, 11)} fill={snow} />
      {/* Sockel av sten */}
      <rect x={-W2} y={-16} width={W2 * 2} height="16" fill={stone} />
      <rect x={-W2} y={-16} width={W2 * 2} height="2" fill={mix("#b9b1a4", night, 0.4)} />
      {/* Pilastrar och fönster i flyglarna */}
      {[-1, 1].map(side => (
        <g key={side}>
          {[P + 10, P + 60, W2 - 50, W2 - 6].map(px => (
            <g key={px}>
              <rect x={side * px - 5} y={wingTop + 6} width="10" height={-wingTop - 22} fill={plasterShade} opacity="0.55" />
              <rect x={side * px - 6.6} y={wingTop + 4} width="13.2" height="4" fill={plaster} />
            </g>
          ))}
          {arched(side * (P + 35), -112, 22, 64, true, `a${side}`)}
          {arched(side * (W2 - 28), -112, 22, 64, side < 0, `b${side}`)}
          {oculus(side * (P + 35), -128, 7, false, `o${side}`)}
          {oculus(side * (W2 - 28), -128, 7, side > 0, `p${side}`)}
          {/* Kartusch över sidoingången */}
          <path d={`M${side * (P + 35) - 8} -42 Q ${side * (P + 35)} -50 ${side * (P + 35) + 8} -42 Z`} fill={plasterShade} />
        </g>
      ))}

      {/* Portiken: frontonen, entablementet och de parställda kolonnerna */}
      <rect x={-P - 4} y={ped} width={(P + 4) * 2} height={-ped} fill={plaster} />
      <path d={`M${-P - 12} ${ped} L0 ${apex} L${P + 12} ${ped} Z`} fill={plaster} />
      <path d={`M${-P + 2} ${ped - 4} L0 ${apex + 10} L${P - 2} ${ped - 4} Z`} fill={plasterShade} opacity="0.55" />
      <path d={`M${-P - 14} ${ped + 1} L0 ${apex - 2} L${P + 14} ${ped + 1}`} stroke={plaster} strokeWidth="4" fill="none" />
      <path d={`M${-P - 16} ${ped + 1.6} L0 ${apex - 4.4} L${P + 16} ${ped + 1.6} L${P + 9} ${ped + 1.6} L0 ${apex + 1} L${-P - 9} ${ped + 1.6} Z`} fill={snow} />
      {/* Kartuschen i frontonen */}
      <ellipse cx="0" cy={ped - 16} rx="16" ry="10" fill={plasterDeep} />
      <ellipse cx="0" cy={ped - 16} rx="12" ry="7" fill={plaster} />
      <path d={`M-22 ${ped - 12} q 6 -8 10 -2 M22 ${ped - 12} q -6 -8 -10 -2`} stroke={plasterDeep} strokeWidth="2" fill="none" strokeLinecap="round" />
      <rect x={-P - 10} y={ped} width={(P + 10) * 2} height="8" fill={plaster} />
      <rect x={-P - 10} y={ped + 8} width={(P + 10) * 2} height="2" fill={plasterDeep} />
      <rect x={-P - 6} y={ped + 10} width={(P + 6) * 2} height="6" fill={plasterShade} />
      <rect x={-P - 6} y={colTop} width={(P + 6) * 2} height="3" fill={plaster} />
      {/* Inskriftstavlan */}
      <rect x="-22" y="-128" width="44" height="18" fill={mix("#5a3a20", night, 0.3)} />
      <rect x="-22" y="-128" width="44" height="18" fill="none" stroke={GOLD} strokeWidth="0.8" opacity="0.8" />
      {range(4).map(i => (
        <path key={i} d={`M-17 ${-123.6 + i * 3.4} H${17 - (i % 2) * 6}`} stroke={GOLD} strokeWidth="0.9" opacity="0.75" />
      ))}
      {[-P, -60, -44, 44, 60, P].map((cx, i) => {
        const pil = i === 0 || i === 5;
        const w = pil ? 9 : 11;
        return (
          <g key={cx}>
            <rect x={cx - w / 2} y={colTop + 4} width={w} height={-colTop - 20} fill={pil ? plasterShade : plaster} />
            {!pil && <rect x={cx + w * 0.1} y={colTop + 4} width={w * 0.4} height={-colTop - 20} fill={plasterShade} opacity="0.7" />}
            {!pil && <rect x={cx - w * 0.36} y={colTop + 6} width={w * 0.14} height={-colTop - 24} fill="#ffffff" opacity="0.3" />}
            <rect x={cx - w / 2 - 2.4} y={colTop + 2} width={w + 4.8} height="5" fill={plaster} />
            <path d={`M${cx - w / 2 - 2.4} ${colTop + 4.4} q 2 3 4 0 M${cx + w / 2 + 2.4} ${colTop + 4.4} q -2 3 -4 0`} stroke={plasterDeep} strokeWidth="1" fill="none" />
            <rect x={cx - w / 2 - 1.6} y={-20} width={w + 3.2} height="4" fill={plaster} />
          </g>
        );
      })}
      {/* Huvudportalen med barockomfattning och upplyst port */}
      <path d="M-16 -16 V-60 Q -16 -74 0 -78 Q 16 -74 16 -60 V-16 Z" fill={plasterShade} />
      <path d="M-11 -16 V-56 H11 V-16 Z" fill="#3a2216" />
      <path d="M-11 -16 V-56 H0 V-16 Z" fill={mix("#ffd9a0", night, 0.1)} opacity="0.85" />
      <path d="M-11 -56 H11" stroke={GOLD} strokeWidth="0.8" opacity="0.8" />
      <circle cx="0" cy="-84" r="4.4" fill={plasterShade} />
      <path d="M-4 -80 q 4 -10 8 0 Z" fill={plaster} />
      {/* Krans på porten */}
      <circle cx="5.5" cy="-40" r="4" fill="none" stroke="#24563a" strokeWidth="2.2" />
      <path d="M4 -36.4 l1.5 -1 l1.5 1 l-0.6 1.6 l-0.9 -1.1 l-0.9 1.1 Z" fill="#d9243a" />
      <path d="M-24 0 H24 L13 -16 H-13 Z" fill="#ffd9a0" opacity="0.32" filter="url(#toon-soft)" />

      {/* Den breda trappan mot Odenplan */}
      {range(4).map(i => (
        <g key={i}>
          <rect x={-94 + i * 6} y={-4 * (i + 1)} width={188 - i * 12} height="4" fill={i % 2 ? stone : mix("#9a9286", night, 0.38)} />
          <path d={snowCap(-94 + i * 6, -60 + i * 6, -4 * (i + 1) - 0.4, 1, i + 40)} fill={snow} />
          <path d={snowCap(60 - i * 6, 94 - i * 6, -4 * (i + 1) - 0.4, 1, i + 50)} fill={snow} />
        </g>
      ))}
      <rect x={-W2 - 6} y={-1} width={W2 * 2 + 12} height="3" fill={snow} opacity="0.8" />
    </g>
  );
};

/* ---------- Gatan ---------- */

export interface TenementSpec {
  x: number;
  w: number;
  h: number;
  wall: string;
  cols: number;
  /** Hörntorn med kopparspira, gavel eller bara mansardtak. */
  roof: "turret" | "gable" | "mansard";
  seed: number;
}

/** Hyreshus från sekelskiftet med snö på taket och adventsljus i fönstren. */
export const Tenement: React.FC<{ spec: TenementSpec; base: number; night: string }> = ({ spec, base, night }) => {
  const { x, w, h, wall, cols, roof, seed } = spec;
  const rnd = seeded(seed);
  const top = base - h;
  const wallC = mix(wall, night, 0.44);
  const shade = mix(wall, night, 0.62);
  const trim = mix(wall, night, 0.3);
  const slate = mix("#161a36", night, 0.5);
  const copper = mix("#6f9a8c", night, 0.45);
  const glass = mix("#0d1130", night, 0.4);
  const floors = 4;
  const fh = (h - 44) / floors;
  const cw = w / cols;
  const winW = Math.min(11, cw * 0.46);
  const winH = fh * 0.58;
  return (
    <g>
      {/* Tak, skorstenar och takkupor */}
      <path d={`M${x - 3} ${top} L${x + 8} ${top - 18} H${x + w - 8} L${x + w + 3} ${top} Z`} fill={slate} />
      <path d={`M${x - 2} ${top - 1.4} L${x + 8.4} ${top - 18.6} H${x + w - 8.4} L${x + w + 2} ${top - 1.4} Z`} fill="url(#vs-roofsnow)" />
      {range(Math.max(2, Math.round(w / 70))).map(i => {
        const cx = x + w * ((i + 0.6) / (Math.round(w / 70) + 1));
        return (
          <g key={i}>
            {range(3).map(k => (
              <circle key={k} className="hol-smoke" cx={cx + 4} cy={top - 34} r={4} fill="#c9cff0" opacity="0.3" style={{ animationDelay: `${-k * 2.6 - i * 1.7 - seed}s` }} />
            ))}
            <rect x={cx} y={top - 32} width="8" height="16" fill={slate} />
            <path d={snowCap(cx - 1, cx + 9, top - 33, 2, seed + i)} fill={SNOW} />
          </g>
        );
      })}
      {range(cols).map(i =>
        i % 2 === 1 ? (
          <g key={i}>
            <path d={`M${x + cw * (i + 0.5) - 6} ${top - 2} V${top - 10} A6 6 0 0 1 ${x + cw * (i + 0.5) + 6} ${top - 10} V${top - 2} Z`} fill={slate} />
            <path d={`M${x + cw * (i + 0.5) - 3.4} ${top - 3} V${top - 9.4} A3.4 3.4 0 0 1 ${x + cw * (i + 0.5) + 3.4} ${top - 9.4} V${top - 3} Z`} fill={rnd() < 0.5 ? LIT : glass} />
            <path d={`M${x + cw * (i + 0.5) - 6.6} ${top - 10} A6.6 6.6 0 0 1 ${x + cw * (i + 0.5) + 6.6} ${top - 10} Q${x + cw * (i + 0.5)} ${top - 15} ${x + cw * (i + 0.5) - 6.6} ${top - 10} Z`} fill={SNOW} />
          </g>
        ) : null
      )}
      {/* Fasaden */}
      <rect x={x} y={top} width={w} height={h + 10} fill={wallC} />
      <rect x={x + w - 6} y={top} width="6" height={h + 10} fill={shade} opacity="0.6" />
      <rect x={x - 2} y={top} width={w + 4} height="5" fill={trim} />
      <rect x={x - 2} y={top + 5} width={w + 4} height="1.6" fill={shade} />
      <path d={snowCap(x - 3, x + w + 3, top - 0.4, 1.6, seed + 9)} fill={SNOW} />
      {/* Bottenvåningen med puts i band och port */}
      <rect x={x} y={base - 40} width={w} height="3" fill={trim} />
      <path d={snowCap(x, x + w, base - 40.4, 1.2, seed + 3)} fill={SNOW} />
      {range(5).map(i => (
        <path key={i} d={`M${x} ${base - 32 + i * 7} H${x + w}`} stroke={shade} strokeWidth="0.8" opacity="0.6" />
      ))}
      {roof === "gable" && (
        <g>
          <path d={`M${x + w * 0.3} ${top + 1} L${x + w / 2} ${top - 40} L${x + w * 0.7} ${top + 1} Z`} fill={wallC} />
          <circle cx={x + w / 2} cy={top - 14} r="5" fill={LIT} opacity="0.85" />
          <path d={`M${x + w * 0.3 - 2} ${top + 1} L${x + w / 2} ${top - 42.4} L${x + w * 0.7 + 2} ${top + 1} L${x + w * 0.7 - 1} ${top + 1} L${x + w / 2} ${top - 38} L${x + w * 0.3 + 1} ${top + 1} Z`} fill={SNOW} />
        </g>
      )}
      {roof === "turret" && (
        <g>
          {/* Burspråk med kopparspira på hörnet */}
          <rect x={x + w - 26} y={top - 6} width="26" height={h - 40} fill={mix(wall, night, 0.48)} />
          <rect x={x + w - 8} y={top - 6} width="8" height={h - 40} fill={shade} opacity="0.5" />
          <path d={`M${x + w - 28} ${top - 6} L${x + w - 13} ${top - 58} L${x + w + 2} ${top - 6} Z`} fill={copper} />
          <path d={`M${x + w - 13} ${top - 58} L${x + w + 2} ${top - 6} H${x + w - 6} Z`} fill={mix(copper, night, 0.4)} />
          <path d={`M${x + w - 13} ${top - 58} V${top - 68}`} stroke={copper} strokeWidth="1.4" />
          <path d={snowCap(x + w - 29, x + w + 3, top - 7, 2.4, seed + 5)} fill={SNOW} />
          <path d={`M${x + w - 24} ${top - 14} L${x + w - 13} ${top - 50} L${x + w - 18} ${top - 16} Z`} fill={SNOW} opacity="0.55" />
          {range(floors).map(f => (
            <LitWindow key={f} x={x + w - 20} y={top + 8 + f * fh} w={12} h={winH} deco={f % 2} />
          ))}
        </g>
      )}
      {/* Våningarnas fönster */}
      {range(floors).map(f =>
        range(cols).map(c => {
          const wx = x + cw * (c + 0.5) - winW / 2;
          if (roof === "turret" && wx > x + w - 30) return null;
          const wy = top + 12 + f * fh;
          const on = rnd() < 0.5;
          const deco = rnd() < 0.55 ? 0 : rnd() < 0.6 ? 1 : 2;
          return (
            <g key={`${f}-${c}`}>
              <rect x={wx - 1.4} y={wy - 1.4} width={winW + 2.8} height={winH + 2.8} fill={trim} opacity="0.7" />
              {on ? <LitWindow x={wx} y={wy} w={winW} h={winH} deco={deco} /> : <rect x={wx} y={wy} width={winW} height={winH} fill={glass} />}
              <path d={snowCap(wx - 2, wx + winW + 2, wy + winH + 1.2, 1.4, f * 31 + c + seed)} fill={SNOW} />
            </g>
          );
        })
      )}
      {/* Porten med krans */}
      <path d={`M${x + w * 0.5 - 9} ${base} V${base - 22} A9 9 0 0 1 ${x + w * 0.5 + 9} ${base - 22} V${base} Z`} fill={mix("#4a2c1a", night, 0.3)} />
      <path d={`M${x + w * 0.5 - 7} ${base - 22} A7 7 0 0 1 ${x + w * 0.5 + 7} ${base - 22} Z`} fill={LIT} opacity="0.8" />
      <circle cx={x + w * 0.5} cy={base - 13} r="4" fill="none" stroke="#2c5e3e" strokeWidth="2" />
      <path d={`M${x + w * 0.5 - 1.6} ${base - 9} l1.6 -1 l1.6 1 l-0.6 1.6 l-1 -1.2 l-1 1.2 Z`} fill="#d9243a" />
      {range(cols)
        .filter(c => Math.abs(c + 0.5 - cols / 2) > 1)
        .map(c => {
          const wx = x + cw * (c + 0.5) - winW / 2;
          return rnd() < 0.6 ? <LitWindow key={c} x={wx} y={base - 32} w={winW} h={18} deco={2} /> : <rect key={c} x={wx} y={base - 32} width={winW} height={18} fill={glass} />;
        })}
    </g>
  );
};

/** Kal lind där snön ligger på grenarnas ovansida. */
export const SnowyTree: React.FC<{ x: number; y: number; len: number; width: number; seed: number; color: string; snow?: string }> = ({ x, y, len, width, seed, color, snow = SNOW }) => {
  const d = bareTree(x, y, len, width, seed, 7);
  return (
    <g>
      <path d={d} fill={snow} transform="translate(0.6 -1.8)" opacity="0.9" />
      <path d={d} fill={color} />
    </g>
  );
};

/** Stockholmslykta av gjutjärn med snö på huven. (x, y) = stolpens fot. */
export const StreetLamp: React.FC<{ x: number; y: number; h?: number; delay?: number }> = ({ x, y, h = 112, delay = 0 }) => {
  const iron = "#0b0d22";
  const top = y - h;
  return (
    <g>
      <circle cx={x} cy={top - 10} r="78" fill="url(#vs-lamp)" className="hol-breathe" style={{ animationDelay: `${delay}s` }} />
      <ellipse cx={x} cy={y + 4} rx="70" ry="9" fill={WARM} opacity="0.2" filter="url(#toon-softer)" />
      <path d={`M${x - 6} ${y} L${x - 4} ${y - 14} H${x + 4} L${x + 6} ${y} Z`} fill={iron} />
      <path d={`M${x - 2} ${y - 14} L${x - 1.4} ${top + 4} H${x + 1.4} L${x + 2} ${y - 14} Z`} fill={iron} />
      <rect x={x - 3.4} y={y - 46} width="6.8" height="3" rx="1" fill={iron} />
      <path d={`M${x - 6} ${top + 4} H${x + 6} L${x + 4} ${top} H${x - 4} Z`} fill={iron} />
      {/* Lyktan */}
      <path d={`M${x - 6.4} ${top} L${x - 8.6} ${top - 20} H${x + 8.6} L${x + 6.4} ${top} Z`} fill="#ffe0a0" />
      <path d={`M${x - 6.4} ${top} L${x - 8.6} ${top - 20} H${x + 8.6} L${x + 6.4} ${top} Z M${x} ${top} V${top - 20}`} fill="none" stroke={iron} strokeWidth="1.2" />
      <circle cx={x} cy={top - 9} r="4" fill="#ffffff" opacity="0.9" />
      <path d={`M${x - 11} ${top - 20} L${x} ${top - 29} L${x + 11} ${top - 20} Z`} fill={iron} />
      <path d={`M${x - 11.6} ${top - 20.4} L${x} ${top - 29.6} L${x + 11.6} ${top - 20.4} Q${x} ${top - 25} ${x - 11.6} ${top - 20.4} Z`} fill={SNOW} />
      <circle cx={x} cy={top - 31.4} r="2" fill={iron} />
    </g>
  );
};

/** Tunnelbaneskylten vid Odenplans uppgång: blått T i en ring på en stolpe. (x, y) = stolpens fot. */
export const SubwaySign: React.FC<{ x: number; y: number }> = ({ x, y }) => (
  <g>
    <circle cx={x} cy={y - 74} r="26" fill="#5aa0ff" opacity="0.22" filter="url(#toon-softer)" className="hol-breathe" />
    <rect x={x - 1.8} y={y - 64} width="3.6" height="64" fill="#1a1c2a" />
    <circle cx={x} cy={y - 74} r="12.5" fill="#ffffff" />
    <circle cx={x} cy={y - 74} r="12.5" fill="none" stroke="#1f5fbf" strokeWidth="2.6" />
    <path d={`M${x - 6.6} ${y - 80.6} H${x + 6.6} V${y - 77.4} H${x + 1.7} V${y - 66} H${x - 1.7} V${y - 77.4} H${x - 6.6} Z`} fill="#1f5fbf" />
    <path d={snowCap(x - 9, x + 9, y - 87, 1.6, 3)} fill={SNOW} />
  </g>
);
